/**
 * CARRYING A JUST-CREATED AREA ACROSS THE SUBSCRIPTION RECONCILE.
 *
 * The areas realtime watcher REPLACES the whole list with the server's snapshot.
 * That snapshot can arrive mid-flight from just BEFORE a create's insert, which
 * wiped the freshly-made area — a card that had just created an area snapped back
 * to "No area", 10 times out of 10 (interaction-witness, 20 Oct; measured).
 *
 * The fix: the store marks a just-created area id PENDING. On each snapshot this
 * carries any pending area the server has NOT yet echoed forward from the current
 * list, and drops the pending mark the moment the server's own row arrives. The
 * carry is BY ID, so the server's copy always wins and no duplicate survives, and
 * — the load-bearing property — a row that is NOT pending is never carried, so an
 * ordinary delete or rename from elsewhere still takes effect.
 */
export function carryPendingAreas<T extends { id: string }>(
  server: T[],
  current: T[],
  pendingIds: string[],
): { areas: T[]; stillPending: string[] } {
  const have = new Set(server.map((a) => a.id));
  const stillPending = pendingIds.filter((id) => !have.has(id));
  const carried = current.filter((a) => stillPending.includes(a.id) && !have.has(a.id));
  return { areas: carried.length ? [...server, ...carried] : server, stillPending };
}

/**
 * ADD A JUST-CREATED ROW ONCE — an idempotent optimistic insert.
 *
 * createArea / createLabel write the row to the backend and THEN insert it into
 * the local list so it shows the instant it is made, without waiting for the
 * realtime snapshot. But the realtime INSERT echo can beat the write's own HTTP
 * ack: the server's row is then ALREADY in the list by the time the optimistic
 * insert runs, so appending unconditionally shows the area/label TWICE until the
 * next snapshot reconciles it (Vinnie, 20 Oct — "it creates the area twice").
 * Guarding by id makes the insert safe whichever arrives first — one row either
 * way. There is still only ever one row in the database; this is the local list.
 */
export function upsertById<T extends { id: string }>(list: T[], row: T): T[] {
  return list.some((a) => a.id === row.id) ? list : [...list, row];
}
